import { useState, useEffect } from "react";
import { Box, CircularProgress, Typography, Alert } from "@mui/material";
import { useCoamAreas } from "../mutations";
import { CoamAreaInfo } from "../components/CoamAreaInfo";
import { CoamAreaFeature } from "../api/coamArea";
import { MapContainer } from "../components/MapContainer";
import { CoamAreasLayer } from "../components/CoamAreasLayer";
import { RagMarkersLayer } from "../components/RagMarkersLayer";
import { useMapInstance } from "../hooks/useMapInstance";

export const Geospatial = () => {
  const [selectedFeature, setSelectedFeature] =
    useState<CoamAreaFeature | null>(null);
  const [features, setFeatures] = useState<CoamAreaFeature[]>([]);
  const [coamAreasError, setCoamAreasError] = useState<string | null>(null);

  const coamAreasMutation = useCoamAreas();
  const { mapContainer, map } = useMapInstance();

  useEffect(() => {
    const fetchCoamAreas = async () => {
      try {
        setCoamAreasError(null);
        const response = await coamAreasMutation.mutateAsync();
        if (response.status && response.data?.features) {
          setFeatures(response.data.features);
        } else {
          setCoamAreasError("Failed to fetch COAM areas");
        }
      } catch (err) {
        console.error("Error fetching COAM areas:", err);
        setCoamAreasError("Failed to load COAM areas");
      }
    };

    fetchCoamAreas();
  }, []);

  const isLoading = coamAreasMutation.isPending;
  const error = coamAreasError;
  const shouldShowMap =
    !coamAreasMutation.isPending && features.length > 0 && !coamAreasError;

  return (
    <Box
      sx={{
        height: "100%",
        width: "100%",
        position: "relative",
        overflow: "hidden",
      }}
    >
      <Box
        sx={{
          display: "flex",
          height: "100%",
          width: "100%",
          zIndex: 10,
          position: "absolute",
          top: 0,
          left: 0,
          pointerEvents: "none",
        }}
      >
        <CoamAreaInfo
          selectedFeature={selectedFeature}
          setSelectedFeature={setSelectedFeature}
        />
      </Box>
      <Box
        sx={{
          position: "absolute",
          top: 0,
          left: 0,
          height: "100%",
          width: "100%",
          zIndex: 0,
          visibility: shouldShowMap ? "visible" : "hidden",
        }}
      >
        <MapContainer ref={mapContainer} />
        <CoamAreasLayer
          map={map}
          features={features}
          selectedFeature={selectedFeature}
          setSelectedFeature={setSelectedFeature}
        />
        <RagMarkersLayer map={map} features={features} />
      </Box>

      {isLoading && (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "100%",
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 2000,
            bgcolor: "background.default",
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 2,
            }}
          >
            <CircularProgress size={60} />
            <Typography variant="body1" color="text.secondary">
              Loading COAM areas...
            </Typography>
          </Box>
        </Box>
      )}

      {error && (
        <Alert
          severity="error"
          sx={{ position: "absolute", top: 16, left: 16, zIndex: 1000 }}
        >
          {error}
        </Alert>
      )}
    </Box>
  );
};
